
import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNotify } from "@/hooks/useNotify";

export interface TeamMember {
  id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  role: string;
  user_type?: string;
  profile_photo_url: string | null;
  company_position: string;
  personal_phone_number?: string | null;
  status: 'active' | 'suspended' | 'deactivated' | 'pending';
  suspended_until?: string | null;
  suspended_by?: string | null;
  suspension_reason?: string | null;
  deactivated_at?: string | null;
  deactivated_by?: string | null;
  deactivation_reason?: string | null;
}

export interface PendingInvitation {
  id: string;
  email: string;
  created_at: string;
  status: string;
}

export const useTeamData = () => {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [companyName, setCompanyName] = useState<string>("");
  const notify = useNotify();
  const { user, profile } = useAuth();

  // Memoize stable user ID to prevent unnecessary effect triggers
  const stableUserId = useMemo(() => user?.id, [user?.id]);

  const fetchTeamMembers = async (companyId: string): Promise<TeamMember[]> => {
    console.log('[TeamData] Fetching team members for company:', companyId);
    
    // First, get all company members (both active and deactivated)
    const { data: companyMembers, error: membersError } = await supabase
      .from('company_members')
      .select('id, user_id, role, status, deactivated_at, deactivated_by, deactivation_reason')
      .eq('company_id', companyId);

    if (membersError) {
      console.error('Error fetching company members:', membersError);
      throw membersError;
    }

    if (!companyMembers || companyMembers.length === 0) {
      return [];
    }

    console.log('[TeamData] Company members found:', companyMembers);
    console.log('[TeamData] Company members details:', companyMembers.map(m => ({
      user_id: m.user_id, 
      role: m.role, 
      status: m.status
    })));

    // Then fetch profiles for all users - this contains the actual company_position
    const userIds = companyMembers.map(member => member.user_id);
    console.log('[TeamData] Fetching profiles for user IDs:', userIds);
    
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
    .select('id, first_name, last_name, email, personal_phone_number, profile_photo_url, company_position, status, suspended_until, suspended_by, suspension_reason')
      .in('id', userIds);

    if (profilesError) {
      console.error('Error fetching profiles:', profilesError);
      throw profilesError;
    }

    console.log('[TeamData] Profiles found:', profiles);
    console.log('[TeamData] Profile details:', profiles?.map(p => ({
      id: p.id,
      name: `${p.first_name} ${p.last_name}`,
      status: p.status,
      suspended_until: p.suspended_until,
      suspension_reason: p.suspension_reason
    })));

    // Also fetch any profiles linked to the company that are missing a company_members row (orphaned)
    const { data: orphanProfiles, error: orphanError } = await supabase
      .from('profiles')
      .select('id, first_name, last_name, email, personal_phone_number, profile_photo_url, company_position, status, suspended_until, suspended_by, suspension_reason')
      .eq('company_id', companyId)
      .not('id', 'in', userIds);

    if (orphanError) {
      console.warn('Error fetching orphaned profiles (will ignore):', orphanError);
    }

    console.log('Orphaned profiles (no company_members row):', orphanProfiles);

    // Combine the data correctly
    const teamMembersFromMembership: TeamMember[] = companyMembers.map(member => {
      const profile = profiles?.find(p => p.id === member.user_id);
      
      // Determine status with updated business rule: 
      // - profiles.status = 'deactivated' means user is deactivated
      // - profiles.status = 'suspended' means user is suspended  
      // - company_members.status = 'deactivated' confirms deactivation at company level
      let memberStatus: 'active' | 'suspended' | 'deactivated' | 'pending';
      
      if (profile?.status === 'deactivated') {
        memberStatus = 'deactivated';
      } else if (profile?.status === 'suspended') {
        memberStatus = 'suspended';
      } else {
        memberStatus = 'active';
      }
      
      // Double-check company member status for consistency
      if (member.status === 'deactivated' && memberStatus !== 'deactivated') {
        console.warn(`[TeamData] INCONSISTENT STATUS: Company member is deactivated but profile status is ${profile?.status}. Using deactivated.`);
        memberStatus = 'deactivated';
      }
      
      console.log(`[TeamData] Processing member ${profile?.first_name} ${profile?.last_name}:`, {
        member_status: member.status,
        profile_status: profile?.status,
        final_status: memberStatus,
        suspended_until: profile?.suspended_until,
        user_id: member.user_id
      });
      
      return {
        id: member.user_id,
        user_id: member.user_id,
        first_name: profile?.first_name || null,
        last_name: profile?.last_name || null,
        email: profile?.email || null,
        role: member.role || 'user',
        user_type: member.role || 'user',
        profile_photo_url: profile?.profile_photo_url || null,
        company_position: profile?.company_position || member.role || 'member',
        personal_phone_number: profile?.personal_phone_number || null,
        status: memberStatus as 'active' | 'suspended' | 'deactivated' | 'pending',
        suspended_until: profile?.suspended_until || null,
        suspended_by: profile?.suspended_by || null,
        suspension_reason: profile?.suspension_reason || null,
        deactivated_at: member.deactivated_at || null,
        deactivated_by: member.deactivated_by || null,
        deactivation_reason: member.deactivation_reason || null
      };
    });

    // Map orphan profiles to team members to ensure visibility in UI
    const orphanedTeamMembers: TeamMember[] = (orphanProfiles || []).map((profile) => {
      return {
        id: profile.id,
        user_id: profile.id,
        first_name: profile.first_name || null,
        last_name: profile.last_name || null,
        email: profile.email || null,
        role: 'user',
        user_type: 'user',
        profile_photo_url: profile.profile_photo_url || null,
        company_position: profile.company_position || 'member',
        personal_phone_number: profile.personal_phone_number || null,
        status: (profile.status || 'active') as 'active' | 'suspended' | 'deactivated' | 'pending',
        suspended_until: profile.suspended_until || null,
        suspended_by: (profile as any).suspended_by || null,
        suspension_reason: profile.suspension_reason || null,
        deactivated_at: null,
        deactivated_by: null,
        deactivation_reason: null
      };
    });

    const teamMembers: TeamMember[] = [...teamMembersFromMembership, ...orphanedTeamMembers];

    console.log('[TeamData] Final team members with correct roles:', teamMembers);
    console.log('[TeamData] Team members summary:', teamMembers.map(m => ({
      name: `${m.first_name} ${m.last_name}`,
      status: m.status,
      role: m.role,
      company_position: m.company_position,
      suspended_until: m.suspended_until
    })));
    
    // Log specifically about suspended users
    const suspendedMembers = teamMembers.filter(m => m.status === 'suspended');
    console.log('[TeamData] Suspended members found:', suspendedMembers.length, suspendedMembers);
    
    return teamMembers;
  };

  const fetchPendingInvitations = async (companyId: string): Promise<PendingInvitation[]> => {
    const { data: invitations, error: invitationsError } = await supabase
      .from('company_invitations')
      .select('id, email, created_at, status')
      .eq('company_id', companyId)
      .eq('status', 'pending');

    if (invitationsError) {
      console.error('Error fetching pending invitations:', invitationsError);
      throw invitationsError;
    }

    console.log('Pending invitations found:', invitations);
    return invitations || [];
  };

  const fetchTeamData = async () => {
    if (!stableUserId) return;
    
    setIsLoading(true);
    
    try {
      const { data: userProfile, error: profileError } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', stableUserId)
        .single();

      if (profileError) throw profileError;
      if (!userProfile?.company_id) {
        setMembers([]);
        setPendingInvitations([]);
        return;
      }

      console.log('[TeamData] Fetching team data for company:', userProfile.company_id);

      const { data: companyData } = await supabase
        .from('companies')
        .select('name')
        .eq('id', userProfile.company_id)
        .single();
      
      if (companyData) {
        setCompanyName(companyData.name);
      }

      // Fetch members and invitations independently - don't let invitation failure break member display
      const [membersResult, invitationsResult] = await Promise.allSettled([
        fetchTeamMembers(userProfile.company_id),
        fetchPendingInvitations(userProfile.company_id)
      ]);

      // Always set members if successful
      if (membersResult.status === 'fulfilled') {
        console.log('[TeamData] Successfully fetched members, setting state:', membersResult.value.length, 'members');
        setMembers(membersResult.value);
      } else {
        console.error('[TeamData] Failed to fetch team members:', membersResult.reason);
        setMembers([]);
      }

      // Set invitations if successful, otherwise empty array (don't break the UI)
      if (invitationsResult.status === 'fulfilled') {
        setPendingInvitations(invitationsResult.value);
      } else {
        console.error('Failed to fetch pending invitations:', invitationsResult.reason);
        setPendingInvitations([]);
      }
    } catch (error) {
      console.error('Error fetching team data:', error);
      notify.error("Failed to load team data");
    } finally {
      setIsLoading(false);
    }
  };

  // Use stableUserId instead of user to prevent unnecessary refetching
  useEffect(() => {
    fetchTeamData();
  }, [stableUserId]);

  useEffect(() => {
    const handleInvitationSent = () => {
      fetchTeamData();
    };

    window.addEventListener('invitationSent', handleInvitationSent);
    return () => {
      window.removeEventListener('invitationSent', handleInvitationSent);
    };
  }, []);

  return {
    members,
    setMembers,
    pendingInvitations,
    setPendingInvitations,
    isLoading,
    companyName,
    companyId: profile?.company_id,
    fetchTeamData
  };
};
